import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

// Helper function to correctly parse UTC dates from backend
// Backend sends dates in formats like "2025-01-07T20:15:00" (without Z) or "2025-01-07T20:15:00+00:00"
// JavaScript's new Date() treats strings without Z/timezone as LOCAL time, causing incorrect display
const parseUTCDate = (dateString) => {
  if (!dateString) return null;
  // If the date string doesn't have a timezone indicator, append 'Z' to treat it as UTC
  if (!dateString.endsWith('Z') && !dateString.includes('+') && !dateString.includes('-', 10)) {
    return new Date(dateString + 'Z');
  }
  return new Date(dateString);
};

const AdminDashboard = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [payments, setPayments] = useState([]);
  const [freeAccessList, setFreeAccessList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Analytics state
  const [journeyAnalytics, setJourneyAnalytics] = useState(null);
  const [revenueAnalytics, setRevenueAnalytics] = useState(null);
  const [engagementAnalytics, setEngagementAnalytics] = useState(null);
  const [trafficAnalytics, setTrafficAnalytics] = useState(null);
  const [yesterdaySummary, setYesterdaySummary] = useState(null);  // Yesterday's visits by page
  const [peakTimesAnalytics, setPeakTimesAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [trafficDays, setTrafficDays] = useState(7);  // Default to 7 days
  
  // Security analytics state (NEW)
  const [securitySummary, setSecuritySummary] = useState(null);
  const [failedLogins, setFailedLogins] = useState(null);
  const [engagementSummary, setEngagementSummary] = useState(null);
  const [topUsersByTime, setTopUsersByTime] = useState(null);
  const [topUsersByLogins, setTopUsersByLogins] = useState(null);
  const [securityLoading, setSecurityLoading] = useState(false);
  
  // Free access form
  const [newEmail, setNewEmail] = useState('');
  const [newNote, setNewNote] = useState('');
  const [addingEmail, setAddingEmail] = useState(false);
  
  // Passcode state
  const [passcodes, setPasscodes] = useState([]);
  const [passcodeStats, setPasscodeStats] = useState(null);
  const [generateCount, setGenerateCount] = useState(1);
  const [batchNote, setBatchNote] = useState('');
  const [generatingPasscodes, setGeneratingPasscodes] = useState(false);
  const [newPasscodes, setNewPasscodes] = useState([]);
  
  const [generatingForRequest, setGeneratingForRequest] = useState(null);
  
  // Notification email setting
  const [notificationEmail, setNotificationEmail] = useState('');
  const [savingNotificationEmail, setSavingNotificationEmail] = useState(false);
  
  // Password change form
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);
  
  // Forced password change modal
  const [showForcePasswordModal, setShowForcePasswordModal] = useState(false);
  const [forcePasswordMessage, setForcePasswordMessage] = useState('');

  const navigate = useNavigate();
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const token = localStorage.getItem('admin_token');
  const adminEmail = localStorage.getItem('admin_email');

  useEffect(() => {
    if (!token) {
      navigate('/admin/login');
      return;
    }
    
    const forceChange = localStorage.getItem('force_password_change');
    const message = localStorage.getItem('password_change_message');
    if (forceChange === 'true') {
      setShowForcePasswordModal(true);
      setForcePasswordMessage(message || 'Please change your default password for security.');
      setActiveTab('settings');
    }
    
    loadData();
  }, []);

  useEffect(() => {
    if (activeTab === 'analytics' && !journeyAnalytics) {
      loadAnalytics();
    }
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === 'security' && !securitySummary) {
      loadSecurityData();
    }
  }, [activeTab]);

  const loadNotificationEmail = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const emailRes = await axios.get(`${BACKEND_URL}/api/admin/settings/notification-email`, { headers });
      setNotificationEmail(emailRes.data.notification_email || '');
    } catch (err) {
      console.error('Failed to load notification email:', err);
    }
  };

  const saveNotificationEmail = async () => {
    setSavingNotificationEmail(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(
        `${BACKEND_URL}/api/admin/settings/notification-email`,
        { email: notificationEmail },
        { headers }
      );
      alert('Notification email updated successfully!');
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to update notification email');
    } finally {
      setSavingNotificationEmail(false);
    }
  };

  const loadData = async () => {
    setLoading(true);
    setError('');
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      const [statsRes, usersRes, paymentsRes, freeAccessRes, passcodesRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/admin/stats`, { headers }).catch(e => { console.error('Stats failed:', e.message); return { data: {} }; }),
        axios.get(`${BACKEND_URL}/api/admin/users`, { headers }).catch(e => { console.error('Users failed:', e.message); return { data: { users: [] } }; }),
        axios.get(`${BACKEND_URL}/api/admin/payments`, { headers }).catch(e => { console.error('Payments failed:', e.message); return { data: { payments: [] } }; }),
        axios.get(`${BACKEND_URL}/api/admin/free-access`, { headers }).catch(e => { console.error('Free access failed:', e.message); return { data: { free_access_list: [] } }; }),
        axios.get(`${BACKEND_URL}/api/admin/passcodes`, { headers }).catch(() => ({ data: { passcodes: [], stats: null } }))
      ]);

      setStats(statsRes.data);
      setUsers(usersRes.data.users);
      setPayments(paymentsRes.data.payments);
      setFreeAccessList(freeAccessRes.data.free_access_list);
      setPasscodes(passcodesRes.data.passcodes || []);
      setPasscodeStats(passcodesRes.data.stats || null);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load data');
      if (err.response?.status === 403 || err.response?.status === 401) {
        localStorage.removeItem('admin_token');
        navigate('/admin/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadAnalytics = async () => {
    setAnalyticsLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      const [journeyRes, revenueRes, engagementRes, trafficRes, peakTimesRes, yesterdayRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/admin/analytics/journey`, { headers }).catch(() => ({ data: null })),
        axios.get(`${BACKEND_URL}/api/admin/analytics/revenue`, { headers }).catch(() => ({ data: null })),
        axios.get(`${BACKEND_URL}/api/admin/analytics/engagement`, { headers }).catch(() => ({ data: null })),
        axios.get(`${BACKEND_URL}/api/admin/analytics/traffic?days=${trafficDays}`, { headers }).catch(() => ({ data: null })),
        axios.get(`${BACKEND_URL}/api/admin/analytics/peak-times`, { headers }).catch(() => ({ data: null })),
        axios.get(`${BACKEND_URL}/api/admin/analytics/yesterday-summary`, { headers }).catch(() => ({ data: null }))
      ]);

      setJourneyAnalytics(journeyRes.data);
      setRevenueAnalytics(revenueRes.data);
      setEngagementAnalytics(engagementRes.data);
      setTrafficAnalytics(trafficRes.data);
      setPeakTimesAnalytics(peakTimesRes.data);
      setYesterdaySummary(yesterdayRes.data);
    } catch (err) {
      console.error('Error loading analytics:', err);
    } finally {
      setAnalyticsLoading(false);
    }
  };

  // Load security and engagement analytics (NEW)
  const loadSecurityData = async () => {
    setSecurityLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      const [securityRes, failedLoginsRes, engagementRes, topTimeRes, topLoginsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/admin/security/summary?hours=24`, { headers }).catch(() => ({ data: null })),
        axios.get(`${BACKEND_URL}/api/admin/security/failed-logins?hours=24`, { headers }).catch(() => ({ data: null })),
        axios.get(`${BACKEND_URL}/api/admin/analytics/engagement-summary?hours=168`, { headers }).catch(() => ({ data: null })),
        axios.get(`${BACKEND_URL}/api/admin/analytics/top-users-by-time?limit=10`, { headers }).catch(() => ({ data: null })),
        axios.get(`${BACKEND_URL}/api/admin/analytics/top-users-by-logins?limit=10`, { headers }).catch(() => ({ data: null }))
      ]);

      setSecuritySummary(securityRes.data);
      setFailedLogins(failedLoginsRes.data);
      setEngagementSummary(engagementRes.data);
      setTopUsersByTime(topTimeRes.data);
      setTopUsersByLogins(topLoginsRes.data);
    } catch (err) {
      console.error('Error loading security data:', err);
    } finally {
      setSecurityLoading(false);
    }
  };

  // Function to reload traffic data with new days
  const reloadTrafficData = async (days) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [trafficRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/admin/analytics/traffic?days=${days}`, { headers })
      ]);
      setTrafficAnalytics(trafficRes.data);
    } catch (err) {
      console.error('Error reloading traffic data:', err);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_email');
    navigate('/admin/login');
  };

  const handleAddFreeAccess = async (e) => {
    e.preventDefault();
    setAddingEmail(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/admin/free-access/add`,
        { email: newEmail, note: newNote },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNewEmail('');
      setNewNote('');
      await loadData();
      alert('Email added to free access list!');
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to add email');
    } finally {
      setAddingEmail(false);
    }
  };

  const handleRemoveFreeAccess = async (email) => {
    if (!window.confirm(`Remove ${email} from free access list?`)) return;
    
    try {
      await axios.delete(
        `${BACKEND_URL}/api/admin/free-access/${encodeURIComponent(email)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await loadData();
      alert('Email removed from free access list!');
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to remove email');
    }
  };

  // Passcode handlers
  const handleGeneratePasscodes = async (e) => {
    e.preventDefault();
    setGeneratingPasscodes(true);
    setNewPasscodes([]);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/admin/passcodes/generate`,
        { 
          count: generateCount,
          batch_note: batchNote || null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNewPasscodes(response.data.passcodes);
      setGenerateCount(1);
      setBatchNote('');
      await loadData();
      alert(`Generated ${response.data.count} passcodes!`);
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to generate passcodes');
    } finally {
      setGeneratingPasscodes(false);
    }
  };

  const copyPasscodesToClipboard = () => {
    if (newPasscodes.length > 0) {
      navigator.clipboard.writeText(newPasscodes.join('\n'));
      alert('Passcodes copied to clipboard!');
    }
  };

  const handleUpdateUserStatus = async (userId, newStatus) => {
    try {
      const sessionExpiry = new Date();
      sessionExpiry.setDate(sessionExpiry.getDate() + 30);
      
      await axios.post(
        `${BACKEND_URL}/api/admin/users/update`,
        { 
          user_id: userId, 
          status: newStatus,
          session_expires_at: sessionExpiry.toISOString()
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await loadData();
      alert('User status updated!');
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to update user');
    }
  };

  const handleGrantNewSession = async (userEmail) => {
    if (!window.confirm(`Grant a NEW session to ${userEmail}?\n\nThis will:\n• Archive their current interpretation\n• Allow them to start fresh journaling\n• Grant 24-hour access`)) {
      return;
    }
    
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/admin/users/grant-new-session?email=${encodeURIComponent(userEmail)}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await loadData();
      alert(response.data.message || 'New session granted successfully!');
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to grant new session');
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      alert('New passwords do not match');
      return;
    }
    
    if (newPassword.length < 6) {
      alert('Password must be at least 6 characters');
      return;
    }
    
    setChangingPassword(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/admin/change-password`,
        { old_password: oldPassword, new_password: newPassword },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      localStorage.removeItem('force_password_change');
      localStorage.removeItem('password_change_message');
      setShowForcePasswordModal(false);
      
      setOldPassword('');
      setNewPassword('');
      setConfirmPassword('');
      alert('Password changed successfully!');
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  const StatCard = ({ title, value, subtitle, color = 'bg-white', highlight = false }) => (
    <div className={`${highlight ? 'bg-red-50 border-red-200' : color} rounded-lg p-4 shadow-sm border ${highlight ? 'border-red-200' : 'border-gray-200'}`}>
      <h3 className="text-sm text-gray-500 mb-1" style={{ fontFamily: 'IBM Plex Serif, serif' }}>{title}</h3>
      <p className={`text-2xl font-bold ${highlight ? 'text-red-600' : 'text-gray-800'}`} style={{ fontFamily: 'IBM Plex Serif, serif' }}>{value}</p>
      {subtitle && <p className="text-xs text-gray-400 mt-1">{subtitle}</p>}
    </div>
  );

  const AnalyticsCard = ({ title, children, className = '' }) => (
    <div className={`bg-white rounded-lg p-4 shadow-sm border border-gray-200 ${className}`}>
      <h3 className="text-lg font-semibold text-gray-800 mb-3" style={{ fontFamily: 'IBM Plex Serif, serif' }}>{title}</h3>
      {children}
    </div>
  );

  const SimpleBarChart = ({ data, labelKey, valueKey, maxValue }) => {
    const max = maxValue || Math.max(...data.map(d => d[valueKey])) || 1;
    return (
      <div className="space-y-2">
        {data.map((item, idx) => (
          <div key={idx} className="flex items-center gap-2">
            <span className="text-xs text-gray-500 w-16 truncate">{item[labelKey]}</span>
            <div className="flex-1 bg-gray-100 rounded h-4">
              <div 
                className="bg-gray-700 h-4 rounded transition-all"
                style={{ width: `${(item[valueKey] / max) * 100}%` }}
              />
            </div>
            <span className="text-xs text-gray-600 w-8 text-right">{item[valueKey]}</span>
          </div>
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-600" style={{ fontFamily: 'IBM Plex Serif, serif' }}>Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">
          <h1 className="text-xl font-bold text-gray-800" style={{ fontFamily: 'IBM Plex Serif, serif' }}>
            Clarity Journal Admin
          </h1>
          <div className="flex items-center gap-4">
            <span className="text-sm text-gray-500">{adminEmail}</span>
            <button
              onClick={handleLogout}
              className="text-sm text-gray-600 hover:text-gray-800 underline"
              style={{ fontFamily: 'IBM Plex Serif, serif' }}
            >
              Logout
            </button>
          </div>
        </div>
      </header>

      {/* Tabs */}
      <div className="max-w-7xl mx-auto px-4 py-4">
        <div className="flex gap-2 border-b border-gray-200 mb-6 overflow-x-auto">
          {['overview', 'analytics', 'security', 'users', 'payments', 'free-access', 'passcodes', 'settings'].map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 text-sm capitalize transition-all whitespace-nowrap ${
                activeTab === tab 
                  ? 'border-b-2 border-gray-800 text-gray-800 font-medium' 
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              style={{ fontFamily: 'IBM Plex Serif, serif' }}
            >
              {tab.replace('-', ' ')}
            </button>
          ))}
        </div>

        {error && (
          <div className="bg-red-50 text-red-600 p-3 rounded-lg mb-4" style={{ fontFamily: 'IBM Plex Serif, serif' }}>
            {error}
          </div>
        )}

        {/* Overview Tab */}
        {activeTab === 'overview' && stats && (
          <div className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatCard title="Total Users" value={stats.total_users} />
              <StatCard title="Paid Users" value={stats.paid_users} />
              <StatCard title="Revenue" value={`$${stats.total_revenue?.toFixed(2) || '0.00'}`} />
              <StatCard title="Free Users" value={stats.free_users} />
              <StatCard title="With Interpretations" value={stats.users_with_interpretations} />
              <StatCard title="Recent Signups (7d)" value={stats.recent_signups} />
              <StatCard title="Active Sessions" value={stats.active_sessions} />
              <StatCard title="Free Access List" value={stats.free_access_count} />
            </div>
          </div>
        )}

        {/* Analytics Tab */}
        {activeTab === 'analytics' && (
          <div className="space-y-6">
            {analyticsLoading ? (
              <div className="text-center py-8 text-gray-500">Loading analytics...</div>
            ) : (
              <>
                {/* Journey Analytics */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <AnalyticsCard title="User Journey Analytics">
                    {journeyAnalytics ? (
                      <div className="space-y-4">
                        <div className="grid grid-cols-3 gap-4 text-center">
                          <div>
                            <p className="text-2xl font-bold text-gray-800">{journeyAnalytics.total_users_started}</p>
                            <p className="text-xs text-gray-500">Started Journey</p>
                          </div>
                          <div>
                            <p className="text-2xl font-bold text-green-600">{journeyAnalytics.total_completed}</p>
                            <p className="text-xs text-gray-500">Completed</p>
                          </div>
                          <div>
                            <p className="text-2xl font-bold text-blue-600">{journeyAnalytics.completion_rate}%</p>
                            <p className="text-xs text-gray-500">Completion Rate</p>
                          </div>
                        </div>
                        
                        <div>
                          <h4 className="text-sm font-medium text-gray-700 mb-2">Drop-off by Prompt</h4>
                          <SimpleBarChart 
                            data={Object.entries(journeyAnalytics.prompt_dropoffs || {}).map(([k, v]) => ({ 
                              prompt: `P${k}`, 
                              dropoffs: v 
                            }))}
                            labelKey="prompt"
                            valueKey="dropoffs"
                          />
                        </div>
                      </div>
                    ) : (
                      <p className="text-gray-400 text-sm">No journey data available</p>
                    )}
                  </AnalyticsCard>

                  <AnalyticsCard title="Conversion Funnel">
                    {revenueAnalytics?.conversion_funnel ? (
                      <div className="space-y-4">
                        {/* Estimated data indicator */}
                        {revenueAnalytics.conversion_funnel.is_estimated && (
                          <p className="text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded">
                            ℹ️ Visit estimates based on signup data (UTM tracking will provide accurate data)
                          </p>
                        )}
                        {/* Visual Funnel */}
                        <div className="space-y-2">
                          <div className="flex items-center gap-2">
                            <div className="w-full bg-gray-200 rounded h-8 relative overflow-hidden">
                              <div className="bg-gray-700 h-8 rounded flex items-center justify-center text-white text-xs font-medium" style={{ width: '100%' }}>
                                Total Visits: {revenueAnalytics.conversion_funnel.total_visits || 0}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="w-full bg-gray-200 rounded h-8 relative overflow-hidden">
                              <div 
                                className="bg-blue-600 h-8 rounded flex items-center justify-center text-white text-xs font-medium" 
                                style={{ 
                                  width: `${Math.min(100, Math.max(10, (revenueAnalytics.conversion_funnel.total_registered / (revenueAnalytics.conversion_funnel.total_visits || 1)) * 100))}%`
                                }}
                              >
                                Sign-ups: {revenueAnalytics.conversion_funnel.total_registered} ({revenueAnalytics.conversion_funnel.visit_to_signup_rate}%)
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="w-full bg-gray-200 rounded h-8 relative overflow-hidden">
                              <div 
                                className="bg-green-600 h-8 rounded flex items-center justify-center text-white text-xs font-medium" 
                                style={{ 
                                  width: `${Math.min(100, Math.max(10, (revenueAnalytics.conversion_funnel.paid_users / (revenueAnalytics.conversion_funnel.total_visits || 1)) * 100))}%`
                                }}
                              >
                                Paid: {revenueAnalytics.conversion_funnel.paid_users} ({revenueAnalytics.conversion_funnel.signup_to_paid_rate}%)
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        {/* Breakdown Stats */}
                        <div className="grid grid-cols-3 gap-2 text-center pt-2 border-t border-gray-100">
                          <div className="p-2 bg-green-50 rounded">
                            <p className="text-lg font-bold text-green-600">{revenueAnalytics.conversion_funnel.paid_users}</p>
                            <p className="text-[10px] text-gray-500">Paid Users</p>
                          </div>
                          <div className="p-2 bg-orange-50 rounded">
                            <p className="text-lg font-bold text-orange-600">{revenueAnalytics.conversion_funnel.unpaid_signups}</p>
                            <p className="text-[10px] text-gray-500">Signed Up, Not Paid</p>
                          </div>
                          <div className="p-2 bg-gray-50 rounded">
                            <p className="text-lg font-bold text-gray-600">{revenueAnalytics.conversion_funnel.no_action || 0}</p>
                            <p className="text-[10px] text-gray-500">No Action</p>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <p className="text-gray-400 text-sm">No conversion data available</p>
                    )}
                  </AnalyticsCard>
                </div>

                {/* Revenue Trends */}
                <AnalyticsCard title="Daily Revenue (Last 30 Days)">
                  {revenueAnalytics?.daily_revenue ? (
                    <div className="overflow-x-auto">
                      <div className="flex gap-1 min-w-max h-32 items-end">
                        {revenueAnalytics.daily_revenue.map((day, idx) => {
                          const maxRev = Math.max(...revenueAnalytics.daily_revenue.map(d => d.revenue)) || 1;
                          const height = (day.revenue / maxRev) * 100;
                          return (
                            <div key={idx} className="flex flex-col items-center group relative">
                              <div 
                                className="w-6 bg-gray-700 rounded-t hover:bg-gray-600 transition-all cursor-pointer"
                                style={{ height: `${Math.max(height, 2)}%` }}
                              />
                              <span className="text-[8px] text-gray-400 rotate-45 origin-left mt-1">
                                {day.date.slice(5)}
                              </span>
                              <div className="absolute bottom-full mb-2 bg-gray-800 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                                ${day.revenue} ({day.transactions} txn)
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ) : (
                    <p className="text-gray-400 text-sm">No revenue data available</p>
                  )}
                </AnalyticsCard>

                {/* Engagement & Traffic */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <AnalyticsCard title="Engagement Analytics">
                    {engagementAnalytics ? (
                      <div className="grid grid-cols-2 gap-4">
                        <div className="text-center p-3 bg-gray-50 rounded">
                          <p className="text-xl font-bold text-gray-800">{engagementAnalytics.pdf_downloads}</p>
                          <p className="text-xs text-gray-500">PDF Downloads</p>
                        </div>
                        <div className="text-center p-3 bg-gray-50 rounded">
                          <p className="text-xl font-bold text-gray-800">{engagementAnalytics.audio_downloads}</p>
                          <p className="text-xs text-gray-500">Audio Downloads</p>
                        </div>
                        <div className="text-center p-3 bg-gray-50 rounded">
                          <p className="text-xl font-bold text-gray-800">{engagementAnalytics.narration_plays}</p>
                          <p className="text-xs text-gray-500">Narration Plays</p>
                        </div>
                        <div className="text-center p-3 bg-gray-50 rounded">
                          <p className="text-xl font-bold text-gray-800">{engagementAnalytics.returning_users}</p>
                          <p className="text-xs text-gray-500">Returning Users</p>
                        </div>
                      </div>
                    ) : (
                      <p className="text-gray-400 text-sm">No engagement data available</p>
                    )}
                  </AnalyticsCard>
                </div>

                {/* Yesterday's Visits Summary */}
                <div className="mt-6">
                  <AnalyticsCard title={`Yesterday's Visits (${yesterdaySummary?.date || 'Loading...'})`}>
                    {yesterdaySummary?.pages?.length > 0 ? (
                      <div className="space-y-3">
                        {yesterdaySummary.pages.map((page, idx) => (
                          <div key={idx} className="flex justify-between items-center py-2 border-b border-gray-100 last:border-b-0">
                            <span className="text-sm font-medium text-gray-700">{page.page_name}</span>
                            <div className="text-right">
                              <span className="text-lg font-semibold text-gray-800">{page.unique_visitors}</span>
                              <span className="text-xs text-gray-400 ml-1">unique</span>
                              <span className="text-sm text-gray-500 ml-2">({page.total_visits} total)</span>
                            </div>
                          </div>
                        ))}
                        <div className="pt-2 mt-2 border-t-2 border-gray-200">
                          <div className="flex justify-between items-center">
                            <span className="text-sm font-semibold text-gray-700">Total (All Pages)</span>
                            <div className="text-right">
                              <span className="text-lg font-bold text-gray-800">{yesterdaySummary.total_unique}</span>
                              <span className="text-xs text-gray-400 ml-1">unique</span>
                              <span className="text-sm text-gray-500 ml-2">({yesterdaySummary.total_visits} total)</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <p className="text-gray-400 text-sm">No visits recorded yesterday.</p>
                    )}
                  </AnalyticsCard>
                </div>

                {/* Traffic Analytics Section */}
                <div className="mt-6">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-lg font-semibold" style={{ fontFamily: 'IBM Plex Serif, serif' }}>
                      Traffic Analytics
                    </h3>
                    <div className="flex items-center gap-2">
                      <label className="text-sm text-gray-600" style={{ fontFamily: 'IBM Plex Serif, serif' }}>
                        Show data for:
                      </label>
                      <select
                        value={trafficDays}
                        onChange={(e) => {
                          const newDays = parseInt(e.target.value);
                          setTrafficDays(newDays);
                          reloadTrafficData(newDays);
                        }}
                        className="px-3 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-gray-400"
                        style={{ fontFamily: 'IBM Plex Serif, serif' }}
                      >
                        <option value={7}>Last 7 days</option>
                        <option value={14}>Last 14 days</option>
                        <option value={30}>Last 30 days</option>
                        <option value={60}>Last 60 days</option>
                        <option value={90}>Last 90 days</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Daily Traffic by Channel */}
                <AnalyticsCard title={`Daily Traffic by Channel (Last ${trafficDays} Days)`} className="mt-4">
                  {trafficAnalytics?.daily_by_channel?.length > 0 ? (
                    <div className="space-y-4">
                      <div className="overflow-x-auto max-h-96 overflow-y-auto">
                        <table className="w-full text-sm">
                          <thead className="sticky top-0 bg-white">
                            <tr className="border-b border-gray-200">
                              <th className="text-left py-2 text-gray-600 font-medium">Date</th>
                              {trafficAnalytics.available_channels?.map(channel => (
                                <th key={channel} className="text-center py-2 text-gray-600 font-medium capitalize">{channel}</th>
                              ))}
                              <th className="text-center py-2 text-gray-600 font-medium">Total</th>
                            </tr>
                          </thead>
                          <tbody>
                            {trafficAnalytics.daily_by_channel.map((day, idx) => (
                              <tr key={idx} className="border-b border-gray-100">
                                <td className="py-2 text-gray-700">
                                  {day.day_name.slice(0, 3)} {day.date.slice(5)}
                                </td>
                                {trafficAnalytics.available_channels?.map(channel => (
                                  <td key={channel} className="py-2 text-center text-gray-600">
                                    {day.channels[channel] || 0}
                                  </td>
                                ))}
                                <td className="py-2 text-center font-medium text-gray-800">{day.total}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      
                      {/* Download Button */}
                      <button
                        onClick={() => {
                          // Generate CSV
                          const channels = trafficAnalytics.available_channels || [];
                          const headers = ['Date', 'Day', ...channels.map(c => c.charAt(0).toUpperCase() + c.slice(1)), 'Total'];
                          const rows = trafficAnalytics.daily_by_channel.map(day => [
                            day.date,
                            day.day_name,
                            ...channels.map(c => day.channels[c] || 0),
                            day.total
                          ]);
                          
                          const csvContent = [
                            headers.join(','),
                            ...rows.map(row => row.join(','))
                          ].join('\n');
                          
                          const blob = new Blob([csvContent], { type: 'text/csv' });
                          const url = window.URL.createObjectURL(blob);
                          const link = document.createElement('a');
                          link.href = url;
                          link.setAttribute('download', `traffic_by_channel_${trafficDays}days_${new Date().toISOString().split('T')[0]}.csv`);
                          document.body.appendChild(link);
                          link.click();
                          link.parentNode.removeChild(link);
                        }}
                        className="px-4 py-2 bg-gray-800 text-white text-sm rounded hover:bg-gray-700 transition-all"
                        style={{ fontFamily: 'IBM Plex Serif, serif' }}
                      >
                        📥 Download CSV
                      </button>
                    </div>
                  ) : (
                    <p className="text-gray-400 text-sm">No daily traffic data available. Traffic will appear here after visitors arrive via UTM-tagged links.</p>
                  )}
                </AnalyticsCard>

                {/* Daily Conversions by Channel (Last 7 Days) */}
                <AnalyticsCard title={`Daily Conversions by Channel (Last ${trafficDays} Days)`} className="mt-4">
                  {trafficAnalytics?.daily_conversions?.length > 0 ? (
                    <div className="space-y-4">
                      <div className="overflow-x-auto max-h-96 overflow-y-auto">
                        <table className="w-full text-sm">
                          <thead className="sticky top-0 bg-white">
                            <tr className="border-b border-gray-200">
                              <th className="text-left py-2 text-gray-600 font-medium">Date</th>
                              {trafficAnalytics.available_channels?.map(channel => (
                                <th key={channel} className="text-center py-2 text-gray-600 font-medium capitalize">{channel}</th>
                              ))}
                              <th className="text-center py-2 text-gray-600 font-medium">Total</th>
                            </tr>
                          </thead>
                          <tbody>
                            {trafficAnalytics.daily_conversions.map((day, idx) => (
                              <tr key={idx} className="border-b border-gray-100">
                                <td className="py-2 text-gray-700">
                                  {day.day_name.slice(0, 3)} {day.date.slice(5)}
                                </td>
                                {trafficAnalytics.available_channels?.map(channel => (
                                  <td key={channel} className={`py-2 text-center ${day.channels[channel] > 0 ? 'text-green-600 font-medium' : 'text-gray-400'}`}>
                                    {day.channels[channel] || 0}
                                  </td>
                                ))}
                                <td className="py-2 text-center font-medium text-green-700">{day.total}</td>
                              </tr>
                            ))}
                            {/* Total row */}
                            <tr className="border-t-2 border-gray-300 bg-gray-50 sticky bottom-0">
                              <td className="py-2 text-gray-800 font-medium">Total ({trafficDays} days)</td>
                              {trafficAnalytics.available_channels?.map(channel => {
                                const total = trafficAnalytics.daily_conversions.reduce((sum, day) => sum + (day.channels[channel] || 0), 0);
                                return (
                                  <td key={channel} className="py-2 text-center font-medium text-green-700">{total}</td>
                                );
                              })}
                              <td className="py-2 text-center font-bold text-green-800">
                                {trafficAnalytics.daily_conversions.reduce((sum, day) => sum + day.total, 0)}
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                      
                      {/* Download Button */}
                      <button
                        onClick={() => {
                          // Generate CSV for conversions
                          const channels = trafficAnalytics.available_channels || [];
                          const headers = ['Date', 'Day', ...channels.map(c => c.charAt(0).toUpperCase() + c.slice(1)), 'Total'];
                          const rows = trafficAnalytics.daily_conversions.map(day => [
                            day.date,
                            day.day_name,
                            ...channels.map(c => day.channels[c] || 0),
                            day.total
                          ]);
                          
                          // Add total row
                          const totalRow = [
                            'TOTAL',
                            `${trafficDays} Days`,
                            ...channels.map(c => trafficAnalytics.daily_conversions.reduce((sum, day) => sum + (day.channels[c] || 0), 0)),
                            trafficAnalytics.daily_conversions.reduce((sum, day) => sum + day.total, 0)
                          ];
                          
                          const csvContent = [
                            headers.join(','),
                            ...rows.map(row => row.join(',')),
                            totalRow.join(',')
                          ].join('\n');
                          
                          const blob = new Blob([csvContent], { type: 'text/csv' });
                          const url = window.URL.createObjectURL(blob);
                          const link = document.createElement('a');
                          link.href = url;
                          link.setAttribute('download', `conversions_by_channel_${trafficDays}days_${new Date().toISOString().split('T')[0]}.csv`);
                          document.body.appendChild(link);
                          link.click();
                          link.parentNode.removeChild(link);
                        }}
                        className="px-4 py-2 bg-green-700 text-white text-sm rounded hover:bg-green-600 transition-all"
                        style={{ fontFamily: 'IBM Plex Serif, serif' }}
                      >
                        📥 Download Conversions CSV
                      </button>
                    </div>
                  ) : (
                    <p className="text-gray-400 text-sm">No daily conversion data available.</p>
                  )}
                </AnalyticsCard>

                {/* Peak Times */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                  <AnalyticsCard title="Activity by Hour (Chicago Time)">
                    {peakTimesAnalytics?.hourly_activity ? (
                      <div>
                        <SimpleBarChart 
                          data={peakTimesAnalytics.hourly_activity.filter((_, i) => i % 2 === 0)}
                          labelKey="hour_label"
                          valueKey="activity"
                        />
                        <p className="text-xs text-gray-500 mt-2">
                          Peak hour: {peakTimesAnalytics.peak_hour_label}
                        </p>
                      </div>
                    ) : (
                      <p className="text-gray-400 text-sm">No peak time data available</p>
                    )}
                  </AnalyticsCard>

                  <AnalyticsCard title="Activity by Day">
                    {peakTimesAnalytics?.daily_activity ? (
                      <div>
                        <SimpleBarChart 
                          data={peakTimesAnalytics.daily_activity}
                          labelKey="day_name"
                          valueKey="activity"
                        />
                        <p className="text-xs text-gray-500 mt-2">
                          Peak day: {peakTimesAnalytics.peak_day}
                        </p>
                      </div>
                    ) : (
                      <p className="text-gray-400 text-sm">No daily data available</p>
                    )}
                  </AnalyticsCard>
                </div>

                {/* Unpaid Users List */}
                <AnalyticsCard title="Users Who Signed Up But Haven't Paid">
                  {revenueAnalytics?.unpaid_users?.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-gray-200">
                            <th className="text-left py-2 text-gray-600">Email</th>
                            <th className="text-left py-2 text-gray-600">Signed Up</th>
                            <th className="text-left py-2 text-gray-600">Last Login</th>
                          </tr>
                        </thead>
                        <tbody>
                          {revenueAnalytics.unpaid_users.map((user, idx) => (
                            <tr key={idx} className="border-b border-gray-100">
                              <td className="py-2 text-gray-800">{user.email}</td>
                              <td className="py-2 text-gray-500">
                                {user.created_at ? parseUTCDate(user.created_at).toLocaleDateString('en-US', {
                                  timeZone: 'America/Chicago',
                                  month: 'short',
                                  day: 'numeric'
                                }) : '-'}
                              </td>
                              <td className="py-2 text-gray-500">
                                {user.last_login ? parseUTCDate(user.last_login).toLocaleDateString('en-US', {
                                  timeZone: 'America/Chicago',
                                  month: 'short',
                                  day: 'numeric'
                                }) : '-'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="text-gray-400 text-sm">All signed up users have paid!</p>
                  )}
                </AnalyticsCard>
              </>
            )}
          </div>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-600" style={{ fontFamily: 'IBM Plex Serif, serif' }}>Email</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-600" style={{ fontFamily: 'IBM Plex Serif, serif' }}>Status</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-600" style={{ fontFamily: 'IBM Plex Serif, serif' }}>Role</th>
                    <th className="px-4 py-3 text-center text-sm font-medium text-gray-600" style={{ fontFamily: 'IBM Plex Serif, serif' }}>Sessions</th>
                    <th className="px-4 py-3 text-center text-sm font-medium text-gray-600" style={{ fontFamily: 'IBM Plex Serif, serif' }}>Logins</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-600" style={{ fontFamily: 'IBM Plex Serif, serif' }}>First Login (Chicago)</th>
                    <th className="px-4 py-3 text-center text-sm font-medium text-gray-600" style={{ fontFamily: 'IBM Plex Serif, serif' }}>Consented</th>
                    <th className="px-4 py-3 text-sm font-medium text-gray-600" style={{ fontFamily: 'IBM Plex Serif, serif' }}>Data Retention</th>
                    <th className="px-4 py-3 text-sm font-medium text-gray-600" style={{ fontFamily: 'IBM Plex Serif, serif' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user, idx) => (
                    <tr key={idx} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm" style={{ fontFamily: 'IBM Plex Serif, serif' }}>{user.email}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 text-xs rounded ${
                          user.status === 'paid' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                        }`}>
                          {user.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm" style={{ fontFamily: 'IBM Plex Serif, serif' }}>{user.role}</td>
                      <td className="px-4 py-3 text-center">
                        <span className={`px-2 py-1 text-xs rounded ${
                          (user.session_count || 0) >= 3 ? 'bg-purple-100 text-purple-700' :
                          (user.session_count || 0) >= 1 ? 'bg-blue-100 text-blue-700' :
                          'bg-gray-100 text-gray-600'
                        }`}>
                          {user.session_count || 0}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`px-2 py-1 text-xs rounded ${
                          (user.login_count || 0) >= 10 ? 'bg-green-100 text-green-700' :
                          (user.login_count || 0) >= 5 ? 'bg-blue-100 text-blue-700' :
                          'bg-gray-100 text-gray-600'
                        }`}>
                          {user.login_count || 0}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm" style={{ fontFamily: 'IBM Plex Serif, serif' }}>
                        {user.first_login_at ? parseUTCDate(user.first_login_at).toLocaleString('en-US', { 
                          timeZone: 'America/Chicago',
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                          hour12: true
                        }) : '-'}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {user.has_consented === true ? (
                          <span className="text-green-600">✓ Yes</span>
                        ) : (
                          <span className="text-gray-400">✗ No</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm" style={{ fontFamily: 'IBM Plex Serif, serif' }}>
                        {user.consent_preferences?.data_retention === 'keep' ? (
                          <span className="text-blue-600">Keep</span>
                        ) : user.consent_preferences?.data_retention === 'delete' ? (
                          <span className="text-orange-600">Delete 24h</span>
                        ) : (
                          <span className="text-gray-300">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {user.role !== 'admin' && (
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleUpdateUserStatus(user.id, user.status === 'paid' ? 'free' : 'paid')}
                              className="text-xs px-3 py-1 bg-gray-800 text-white rounded hover:bg-gray-700"
                              style={{ fontFamily: 'IBM Plex Serif, serif' }}
                            >
                              {user.status === 'paid' ? 'Revoke' : 'Grant'} Access
                            </button>
                            <button
                              onClick={() => handleGrantNewSession(user.email)}
                              className="text-xs px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700"
                              style={{ fontFamily: 'IBM Plex Serif, serif' }}
                              title="Grant a fresh journaling session (archives old interpretation)"
                            >
                              New Session
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Payments Tab */}
        {activeTab === 'payments' && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-600" style={{ fontFamily: 'IBM Plex Serif, serif' }}>Date</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-600" style={{ fontFamily: 'IBM Plex Serif, serif' }}>User Email</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-600" style={{ fontFamily: 'IBM Plex Serif, serif' }}>Amount</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-600" style={{ fontFamily: 'IBM Plex Serif, serif' }}>Status</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-600" style={{ fontFamily: 'IBM Plex Serif, serif' }}>Session ID</th>
                  </tr>
                </thead>
                <tbody>
                  {payments.map((payment, idx) => (
                    <tr key={idx} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm" style={{ fontFamily: 'IBM Plex Serif, serif' }}>
                        {parseUTCDate(payment.created_at).toLocaleString('en-US', {
                          timeZone: 'America/Chicago',
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </td>
                      <td className="px-4 py-3 text-sm" style={{ fontFamily: 'IBM Plex Serif, serif' }}>{payment.user_email || payment.email || 'Unknown'}</td>
                      <td className="px-4 py-3 text-sm font-medium" style={{ fontFamily: 'IBM Plex Serif, serif' }}>${payment.amount?.toFixed(2)}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 text-xs rounded ${
                          payment.payment_status === 'paid' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {payment.payment_status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-500 font-mono" style={{ fontFamily: 'monospace' }}>
                        {payment.session_id?.substring(0, 20)}...
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Free Access Tab */}
        {activeTab === 'free-access' && (
          <div className="space-y-6">
            {/* Add Email Form */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <h2 className="text-lg font-semibold mb-4" style={{ fontFamily: 'IBM Plex Serif, serif' }}>Add Free Access</h2>
              <form onSubmit={handleAddFreeAccess} className="flex gap-4 flex-wrap">
                <input
                  type="email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  placeholder="Email address"
                  required
                  className="flex-1 min-w-[200px] px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-gray-400"
                  style={{ fontFamily: 'IBM Plex Serif, serif' }}
                />
                <input
                  type="text"
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                  placeholder="Note (optional)"
                  className="flex-1 min-w-[200px] px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-gray-400"
                  style={{ fontFamily: 'IBM Plex Serif, serif' }}
                />
                <button
                  type="submit"
                  disabled={addingEmail}
                  className="px-4 py-2 bg-gray-800 text-white rounded hover:bg-gray-700 disabled:opacity-50"
                  style={{ fontFamily: 'IBM Plex Serif, serif' }}
                >
                  {addingEmail ? 'Adding...' : 'Add Email'}
                </button>
              </form>
            </div>

            {/* Free Access List */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-600" style={{ fontFamily: 'IBM Plex Serif, serif' }}>Email</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-600" style={{ fontFamily: 'IBM Plex Serif, serif' }}>Note</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-600" style={{ fontFamily: 'IBM Plex Serif, serif' }}>Added</th>
                      <th className="px-4 py-3 text-sm font-medium text-gray-600" style={{ fontFamily: 'IBM Plex Serif, serif' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {freeAccessList.map((item, idx) => (
                      <tr key={idx} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm" style={{ fontFamily: 'IBM Plex Serif, serif' }}>{item.email}</td>
                        <td className="px-4 py-3 text-sm text-gray-500" style={{ fontFamily: 'IBM Plex Serif, serif' }}>{item.note || '-'}</td>
                        <td className="px-4 py-3 text-sm text-gray-500" style={{ fontFamily: 'IBM Plex Serif, serif' }}>
                          {item.added_at ? parseUTCDate(item.added_at).toLocaleDateString('en-US', {
                            timeZone: 'America/Chicago',
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric'
                          }) : '-'}
                        </td>
                        <td className="px-4 py-3">
                          <button
                            onClick={() => handleRemoveFreeAccess(item.email)}
                            className="text-xs px-3 py-1 bg-red-100 text-red-600 rounded hover:bg-red-200"
                            style={{ fontFamily: 'IBM Plex Serif, serif' }}
                          >
                            Remove
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Passcodes Tab */}
        {activeTab === 'passcodes' && (
          <div className="space-y-6">
            {/* Info Banner */}
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
              <p className="text-purple-800 text-sm" style={{ fontFamily: 'IBM Plex Serif, serif' }}>
                Passcodes allow you to gift session access to clients. Each passcode can be used once. 
                Clients enter their passcode on the landing page to begin their session.
              </p>
            </div>

            {/* Stats Cards */}
            {passcodeStats && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 text-center">
                  <p className="text-2xl font-bold text-gray-800">{passcodeStats.total}</p>
                  <p className="text-sm text-gray-600" style={{ fontFamily: 'IBM Plex Serif, serif' }}>Total Passcodes</p>
                </div>
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 text-center">
                  <p className="text-2xl font-bold text-green-600">{passcodeStats.used}</p>
                  <p className="text-sm text-gray-600" style={{ fontFamily: 'IBM Plex Serif, serif' }}>Used</p>
                </div>
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 text-center">
                  <p className="text-2xl font-bold text-blue-600">{passcodeStats.unused}</p>
                  <p className="text-sm text-gray-600" style={{ fontFamily: 'IBM Plex Serif, serif' }}>Available</p>
                </div>
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 text-center">
                  <p className="text-2xl font-bold text-purple-600">{passcodeStats.completed}</p>
                  <p className="text-sm text-gray-600" style={{ fontFamily: 'IBM Plex Serif, serif' }}>Sessions Completed</p>
                </div>
              </div>
            )}

            {/* Generate Passcodes Form */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <h2 className="text-lg font-semibold mb-4" style={{ fontFamily: 'IBM Plex Serif, serif' }}>
                Generate New Passcodes
              </h2>
              <form onSubmit={handleGeneratePasscodes} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1" style={{ fontFamily: 'IBM Plex Serif, serif' }}>
                      Number of Passcodes
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="100"
                      value={generateCount}
                      onChange={(e) => setGenerateCount(parseInt(e.target.value) || 1)}
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-gray-400"
                      style={{ fontFamily: 'IBM Plex Serif, serif' }}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1" style={{ fontFamily: 'IBM Plex Serif, serif' }}>
                      Batch Note (optional)
                    </label>
                    <input
                      type="text"
                      value={batchNote}
                      onChange={(e) => setBatchNote(e.target.value)}
                      placeholder="e.g., Q1 2025 Client Gifts"
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-gray-400"
                      style={{ fontFamily: 'IBM Plex Serif, serif' }}
                    />
                  </div>
                </div>
                <button
                  type="submit"
                  disabled={generatingPasscodes}
                  className="px-6 py-2 bg-gray-800 text-white rounded hover:bg-gray-700 disabled:opacity-50"
                  style={{ fontFamily: 'IBM Plex Serif, serif' }}
                >
                  {generatingPasscodes ? 'Generating...' : `Generate ${generateCount} Passcode${generateCount > 1 ? 's' : ''}`}
                </button>
              </form>

              {/* Newly Generated Passcodes */}
              {newPasscodes.length > 0 && (
                <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex justify-between items-center mb-2">
                    <h4 className="font-medium text-green-800" style={{ fontFamily: 'IBM Plex Serif, serif' }}>
                      Newly Generated Passcodes:
                    </h4>
                    <button
                      onClick={copyPasscodesToClipboard}
                      className="text-sm px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700"
                      style={{ fontFamily: 'IBM Plex Serif, serif' }}
                    >
                      Copy All
                    </button>
                  </div>
                  <div className="font-mono text-sm bg-white p-3 rounded border border-green-300 max-h-40 overflow-y-auto">
                    {newPasscodes.map((code, idx) => (
                      <div key={idx} className="text-green-800">{code}</div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Passcodes Table */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
                <h3 className="font-semibold text-gray-700" style={{ fontFamily: 'IBM Plex Serif, serif' }}>
                  All Passcodes ({passcodes.length})
                </h3>
              </div>
              <div className="overflow-x-auto">
                {passcodes.length === 0 ? (
                  <div className="p-8 text-center text-gray-500" style={{ fontFamily: 'IBM Plex Serif, serif' }}>
                    No passcodes generated yet. Create some above.
                  </div>
                ) : (
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-600" style={{ fontFamily: 'IBM Plex Serif, serif' }}>Passcode</th>
                        <th className="px-4 py-3 text-center text-sm font-medium text-gray-600" style={{ fontFamily: 'IBM Plex Serif, serif' }}>Status</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-600" style={{ fontFamily: 'IBM Plex Serif, serif' }}>Used At</th>
                        <th className="px-4 py-3 text-center text-sm font-medium text-gray-600" style={{ fontFamily: 'IBM Plex Serif, serif' }}>Session Done</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-600" style={{ fontFamily: 'IBM Plex Serif, serif' }}>Created</th>
                      </tr>
                    </thead>
                    <tbody>
                      {passcodes.map((pc, idx) => (
                        <tr key={idx} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="px-4 py-3 font-mono text-sm">{pc.passcode}</td>
                          <td className="px-4 py-3 text-center">
                            <span className={`px-2 py-1 text-xs rounded ${
                              pc.status === 'used' ? 'bg-green-100 text-green-800' : 
                              pc.status === 'expired' ? 'bg-red-100 text-red-800' : 
                              'bg-blue-100 text-blue-800'
                            }`}>
                              {pc.status}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm" style={{ fontFamily: 'IBM Plex Serif, serif' }}>
                            {pc.used_at ? new Date(pc.used_at).toLocaleDateString() : '-'}
                          </td>
                          <td className="px-4 py-3 text-center">
                            {pc.session_completed ? (
                              <span className="text-green-600">✓</span>
                            ) : pc.status === 'used' ? (
                              <span className="text-yellow-600">⏳</span>
                            ) : (
                              <span className="text-gray-300">-</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-500" style={{ fontFamily: 'IBM Plex Serif, serif' }}>
                            {pc.created_at ? new Date(pc.created_at).toLocaleDateString() : '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Security Tab (NEW) */}
        {activeTab === 'security' && (
          <div className="space-y-6">
            {securityLoading ? (
              <div className="text-center py-8 text-gray-500" style={{ fontFamily: 'IBM Plex Serif, serif' }}>Loading security data...</div>
            ) : (
              <>
                {/* Security Summary Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <StatCard 
                    title="Total Events (24h)" 
                    value={securitySummary?.total_events || 0} 
                  />
                  <StatCard 
                    title="Failed Logins" 
                    value={securitySummary?.event_counts?.login_failed || 0} 
                    highlight={securitySummary?.event_counts?.login_failed > 10}
                  />
                  <StatCard 
                    title="Unique IPs (Failed)" 
                    value={securitySummary?.failed_login_ips_count || 0} 
                  />
                  <StatCard 
                    title="Password Resets" 
                    value={securitySummary?.event_counts?.password_reset_request || 0} 
                  />
                </div>

                {/* Failed Logins by IP */}
                {failedLogins?.by_ip?.length > 0 && (
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                    <h3 className="font-semibold mb-3" style={{ fontFamily: 'IBM Plex Serif, serif' }}>
                      Failed Logins by IP (Last 24h)
                    </h3>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b">
                            <th className="text-left py-2 px-3">IP Address</th>
                            <th className="text-left py-2 px-3">Attempts</th>
                            <th className="text-left py-2 px-3">Target Emails</th>
                            <th className="text-left py-2 px-3">Last Attempt</th>
                          </tr>
                        </thead>
                        <tbody>
                          {failedLogins.by_ip.slice(0, 10).map((item, idx) => (
                            <tr key={idx} className={`border-b ${item.count >= 5 ? 'bg-red-50' : ''}`}>
                              <td className="py-2 px-3 font-mono text-xs">{item.ip}</td>
                              <td className="py-2 px-3">
                                <span className={`px-2 py-1 rounded text-xs ${item.count >= 5 ? 'bg-red-100 text-red-700' : 'bg-gray-100'}`}>
                                  {item.count}
                                </span>
                              </td>
                              <td className="py-2 px-3 text-xs">{item.emails?.slice(0, 3).join(', ')}{item.emails?.length > 3 ? '...' : ''}</td>
                              <td className="py-2 px-3 text-xs text-gray-500">{item.last_attempt ? new Date(item.last_attempt).toLocaleString() : '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Recent Security Alerts */}
                {securitySummary?.recent_alerts?.length > 0 && (
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                    <h3 className="font-semibold mb-3" style={{ fontFamily: 'IBM Plex Serif, serif' }}>
                      Recent Security Alerts
                    </h3>
                    <div className="space-y-2">
                      {securitySummary.recent_alerts.slice(0, 10).map((alert, idx) => (
                        <div key={idx} className={`p-3 rounded text-sm ${
                          alert.severity === 'critical' ? 'bg-red-50 border-l-4 border-red-500' :
                          alert.severity === 'warning' ? 'bg-yellow-50 border-l-4 border-yellow-500' :
                          'bg-gray-50 border-l-4 border-gray-300'
                        }`}>
                          <div className="flex justify-between">
                            <span className="font-medium">{alert.event_type?.replace(/_/g, ' ')}</span>
                            <span className="text-xs text-gray-500">{new Date(alert.timestamp).toLocaleString()}</span>
                          </div>
                          <div className="text-xs text-gray-600 mt-1">
                            {alert.email && <span>Email: {alert.email}</span>}
                            {alert.ip_address && <span className="ml-3">IP: {alert.ip_address}</span>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Engagement Analytics */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                  <h3 className="font-semibold mb-3" style={{ fontFamily: 'IBM Plex Serif, serif' }}>
                    Engagement Analytics (Last 7 Days)
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="text-center p-3 bg-gray-50 rounded">
                      <div className="text-2xl font-bold text-gray-800">{engagementSummary?.narration?.total_plays || 0}</div>
                      <div className="text-xs text-gray-500">Narration Plays</div>
                    </div>
                    <div className="text-center p-3 bg-gray-50 rounded">
                      <div className="text-2xl font-bold text-gray-800">{engagementSummary?.narration?.completion_rate || 0}%</div>
                      <div className="text-xs text-gray-500">Completion Rate</div>
                    </div>
                    <div className="text-center p-3 bg-gray-50 rounded">
                      <div className="text-2xl font-bold text-gray-800">{engagementSummary?.downloads?.pdf_downloads || 0}</div>
                      <div className="text-xs text-gray-500">PDF Downloads</div>
                    </div>
                    <div className="text-center p-3 bg-gray-50 rounded">
                      <div className="text-2xl font-bold text-gray-800">{engagementSummary?.downloads?.audio_downloads || 0}</div>
                      <div className="text-xs text-gray-500">Audio Downloads</div>
                    </div>
                  </div>
                  
                  {/* Session Duration Distribution */}
                  {engagementSummary?.sessions?.duration_distribution && (
                    <div className="mt-4">
                      <h4 className="text-sm font-medium mb-2">Session Duration Distribution</h4>
                      <div className="flex gap-2">
                        {Object.entries(engagementSummary.sessions.duration_distribution).map(([range, count]) => (
                          <div key={range} className="flex-1 text-center p-2 bg-blue-50 rounded text-xs">
                            <div className="font-bold text-blue-700">{count}</div>
                            <div className="text-gray-500">{range.replace(/_/g, ' ')}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Top Users Section - Side by Side */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Top 10 by Total Time Spent */}
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                    <h3 className="font-semibold mb-3 flex items-center gap-2" style={{ fontFamily: 'IBM Plex Serif, serif' }}>
                      <span>⏱️</span> Top 10 by Time Spent
                    </h3>
                    {topUsersByTime?.top_users_by_time?.length > 0 ? (
                      <div className="space-y-2">
                        {topUsersByTime.top_users_by_time.map((user, idx) => (
                          <div key={idx} className="flex justify-between items-center p-2 bg-gray-50 rounded text-sm">
                            <div className="flex items-center gap-2">
                              <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                                idx === 0 ? 'bg-yellow-400 text-yellow-900' :
                                idx === 1 ? 'bg-gray-300 text-gray-700' :
                                idx === 2 ? 'bg-amber-600 text-white' :
                                'bg-gray-100 text-gray-600'
                              }`}>{idx + 1}</span>
                              <span className="truncate max-w-[150px]" title={user.email}>{user.email}</span>
                            </div>
                            <div className="text-right">
                              <div className="font-bold text-blue-600">{user.total_time_minutes} min</div>
                              <div className="text-xs text-gray-400">{user.session_count} sessions</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center text-gray-400 py-4 text-sm">No session data yet</div>
                    )}
                  </div>

                  {/* Top 10 by Login Count */}
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                    <h3 className="font-semibold mb-3 flex items-center gap-2" style={{ fontFamily: 'IBM Plex Serif, serif' }}>
                      <span>🔑</span> Top 10 by Logins
                    </h3>
                    {topUsersByLogins?.top_users_by_logins?.length > 0 ? (
                      <div className="space-y-2">
                        {topUsersByLogins.top_users_by_logins.map((user, idx) => (
                          <div key={idx} className="flex justify-between items-center p-2 bg-gray-50 rounded text-sm">
                            <div className="flex items-center gap-2">
                              <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                                idx === 0 ? 'bg-yellow-400 text-yellow-900' :
                                idx === 1 ? 'bg-gray-300 text-gray-700' :
                                idx === 2 ? 'bg-amber-600 text-white' :
                                'bg-gray-100 text-gray-600'
                              }`}>{idx + 1}</span>
                              <span className="truncate max-w-[150px]" title={user.email}>{user.email}</span>
                            </div>
                            <div className="text-right">
                              <div className="font-bold text-green-600">{user.login_count} logins</div>
                              <div className="text-xs text-gray-400">{user.status}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center text-gray-400 py-4 text-sm">No login data yet</div>
                    )}
                  </div>
                </div>

                {/* Refresh Button */}
                <div className="text-center">
                  <button
                    onClick={loadSecurityData}
                    className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded text-sm transition-colors"
                    style={{ fontFamily: 'IBM Plex Serif, serif' }}
                  >
                    Refresh Security Data
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {/* Settings Tab */}
        {activeTab === 'settings' && (
          <div className="max-w-md mx-auto">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold mb-4" style={{ fontFamily: 'IBM Plex Serif, serif' }}>Change Password</h2>
              <p className="text-sm text-gray-500 mb-4" style={{ fontFamily: 'IBM Plex Serif, serif' }}>
                Update your admin password to keep your account secure.
              </p>
              <form onSubmit={handlePasswordChange} className="space-y-4">
                <div>
                  <label className="block text-sm text-gray-600 mb-1" style={{ fontFamily: 'IBM Plex Serif, serif' }}>Current Password</label>
                  <input
                    type="password"
                    value={oldPassword}
                    onChange={(e) => setOldPassword(e.target.value)}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-gray-400"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1" style={{ fontFamily: 'IBM Plex Serif, serif' }}>New Password</label>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    minLength={6}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-gray-400"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1" style={{ fontFamily: 'IBM Plex Serif, serif' }}>Confirm New Password</label>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    minLength={6}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-gray-400"
                  />
                </div>
                <button
                  type="submit"
                  disabled={changingPassword}
                  className="w-full px-4 py-2 bg-gray-800 text-white rounded hover:bg-gray-700 disabled:opacity-50"
                  style={{ fontFamily: 'IBM Plex Serif, serif' }}
                >
                  {changingPassword ? 'Changing...' : 'Change Password'}
                </button>
              </form>
            </div>
          </div>
        )}
      </div>

      {/* Force Password Change Modal */}
      {showForcePasswordModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md mx-4">
            <h2 className="text-lg font-semibold mb-2" style={{ fontFamily: 'IBM Plex Serif, serif' }}>Password Change Required</h2>
            <p className="text-sm text-gray-600 mb-4" style={{ fontFamily: 'IBM Plex Serif, serif' }}>
              {forcePasswordMessage}
            </p>
            <p className="text-sm text-gray-500" style={{ fontFamily: 'IBM Plex Serif, serif' }}>
              Please use the Settings tab to change your password.
            </p>
            <button
              onClick={() => setShowForcePasswordModal(false)}
              className="mt-4 w-full px-4 py-2 bg-gray-800 text-white rounded hover:bg-gray-700"
              style={{ fontFamily: 'IBM Plex Serif, serif' }}
            >
              Go to Settings
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
